class ConfiguratorApp {
  constructor($el) {
    this.$el = $el;
    this.src = this.$el.data('env') != 'dev' ? 'vue.esm-browser.prod' : 'vue.esm-browser';
    this.taxRate = Number(this.$el.data('tax-rate'));
    this.quantity = Number(this.$el.data('quantity'));

    // defaults measurements
    this.height = Number(this.$el.data('height'));
    this.width = Number(this.$el.data('width'));
    this.depth = Number(this.$el.data('depth'));
    this.frameWidth = Number(this.$el.data('frame-width'));

    this.priceCategories = this.$el.data('price-categories');
    this.woodTypes = this.$el.data('wood-types');
    this.surfaces = this.$el.data('surfaces');
    this.handles = this.$el.data('handles');
    this.structureDirections = this.$el.data('structure-directions');

    this.heights = this.$el.data('heights');
    this.widths = this.$el.data('widths');
    this.frameWidths = this.$el.data('frame-widths');


    this.init();
  }

  init = () => {
    // dynamic import from vendor vue.global.js
    import(`../vendor/${this.src}`).then(({ createApp }) => {
      const _this = this;
      this.app = createApp({
        delimiters: ['[[', ']]'],
        data() {
          return {
            step: 1,
            config: {
              priceCategories: _this.priceCategories,
              taxRate: _this.taxRate,
              woodTypes: _this.woodTypes,
              surfaces: _this.surfaces,
              structureDirections: _this.structureDirections,
              handles: _this.handles,
              heights: _this.heights,
              widths: _this.widths,
              frameWidths: _this.frameWidths,
            },
            inputs: {
              quantity: _this.quantity,
              woodTypeId: null,
              surfaceId: null,
              handleId: null,
              structureDirectionId: null,
              height: _this.height,
              width: _this.width,
              depth: _this.depth,
              frameWidth: _this.frameWidth,
            },
            formSuccess: false,
          };
        },
        computed: {
          currentPriceCategory() {
            return this.config.priceCategories.find((priceCategory) => {
              return this.inputs.quantity >= priceCategory.quantityMin && (priceCategory.quantityMax == null || this.inputs.quantity <= priceCategory.quantityMax);
            });
          },
          currentWoodType() {
            return this.config.woodTypes.find((woodType) => {
              return woodType.id == this.inputs.woodTypeId;
            });
          },
          woodPrice() {
            if(!this.currentWoodType) {
              return 0;
            }
            let price = this.currentWoodType?.price; // price is defined for the measurements 800x2100
            price = (price * this.inputs.height * this.inputs.width) / (800*2100);  
            return price;
          },
          currentSurface() {
            return this.config.surfaces.find((surface) => {
              return surface.id == this.inputs.surfaceId;
            });
          },
          currentHandle() {
            return this.config.handles.find((handle) => {
              return handle.id == this.inputs.handleId;
            });
          },
          currentStructureDirection() {
            return this.config.structureDirections.find((structureDirection) => {
              return structureDirection.id == this.inputs.structureDirectionId;
            });
          },
          currentHeight() {
            return this.config.heights.find((heightCategory) => {
              return this.inputs.height >= heightCategory.heightMin && (heightCategory.heightMax == null || this.inputs.height <= heightCategory.heightMax);
            });
          },
          currentDepth() {
            return this.currentWoodType?.depths.find((depthCategory) => {
              return this.inputs.depth >= depthCategory.depthMin && (depthCategory.depthMax == null || this.inputs.depth <= depthCategory.depthMax);
            });
          },
          currentWidth() {
            return this.config.widths.find((widthCategory) => {
              return this.inputs.width >= widthCategory.widthMin && (widthCategory.widthMax == null || this.inputs.width <= widthCategory.widthMax);
            });
          },
          currentFrameWidth() {
            return this.config.frameWidths.find((frameWidthCategory) => {
              return this.inputs.frameWidth >= frameWidthCategory.frameWidthMin && (frameWidthCategory.frameWidthMax == null || this.inputs.frameWidth <= frameWidthCategory.frameWidthMax);
            });
          },
          singlePrice() {
            return this.currentPriceCategory.price +
                    this.woodPrice +
                    this.currentSurface?.price +
                    this.currentHandle?.price +
                    this.currentStructureDirection?.price +
                    this.currentHeight?.price + 
                    this.currentDepth?.price +
                    this.currentWidth?.price +
                    this.currentFrameWidth?.price;
          },
          totalPrice() {
            return this.singlePrice * this.inputs.quantity;
          },
          taxes () {
            return this.totalPrice * this.config.taxRate;
          },
          totalPriceWithTaxes() {
            return this.totalPrice + this.taxes;
          },
          textSummary() {
            let text = '';
            if(this.currentWoodType) {
              text += this.currentWoodType.title;
            }
            if(this.currentSurface) {
              text += ', ' + this.currentSurface.title;
            }
            if(this.currentStructureDirection) {
              text += ', ' + this.currentStructureDirection.title;
            }
            if(this.currentHandle) {
              text += ', ' + this.currentHandle.title;
            }
            return text;
          },
          maxHeight() {
            return Math.max(...this.config.heights.map((heightCategory) => heightCategory.heightMax));
          },
          maxWidth() {
            return Math.max(...this.config.widths.map((widthCategory) => widthCategory.widthMax));
          },
          maxDepth() {
            return Math.max(...this.currentWoodType?.depths.map((depthCategory) => depthCategory.depthMax));
          },
          maxFrameWidth() {
            return Math.max(...this.config.frameWidths.map((frameWidthCategory) => frameWidthCategory.frameWidthMax));
          },
        },
        mounted() {
          this.inputs.woodTypeId = this.config.woodTypes[0]?.id;
          this.inputs.surfaceId = this.config.surfaces[0]?.id;
          this.inputs.handleId = this.config.handles[0]?.id;
          this.inputs.structureDirectionId = this.config.structureDirections[0]?.id;

          _this.$el.removeClass('opacity-0');
        },
        methods: {
          formatPrice(value) {
            // Runden Sie den Wert auf fünf Cent
            const roundedValue = (Math.ceil(value * 20) / 20).toFixed(2);
            // Konvertieren Sie den Wert in das gewünschte Format
            const formattedValue = new Intl.NumberFormat('de-CH', { style: 'currency', currency: 'CHF' }).format(roundedValue);
            return formattedValue;
          },
          increment () {
            this.inputs.quantity++;
          },
          decrement () {
            if(this.inputs.quantity > 1) {
              this.inputs.quantity--;
            }
          },
          submitForm(e) {
            e.preventDefault();

            const form = e.target;

            const data = new FormData(form);
		        let request = new XMLHttpRequest();

            const method = form.getAttribute('method');
            const action = form.getAttribute('action');

            request.open(method, action ? action : window.location.href, true);
            request.setRequestHeader('Cache-Control', 'no-cache');
            request.setRequestHeader('X-Requested-With', 'XMLHttpRequest');
            request.setRequestHeader('HTTP_X_REQUESTED_WITH', 'XMLHttpRequest');
            request.setRequestHeader('Accept', 'application/json');

            this.$refs.submitButton.disabled = true;

            request.onload = () => {
              console.log('request', request);
              // this.removeErrors();
              if (request.status === 200) {
                const response = JSON.parse(request.response);
        
                console.log('response', response);
        
                if (response.success) {
                  this.formSuccess = true;
                } else if (response.errors) {
                  //this.showErrors(response.errors);
                }
              } else {
                console.error(request);
              }
              this.$refs.submitButton.disabled = false;
            };
        
            request.send(data);
          },
        },
        watch: {
          'inputs.height': function (newVal) {
            if(newVal > this.maxHeight) {
              this.inputs.height = this.maxHeight;
            }
          },
          'inputs.width': function (newVal) {
            if(newVal > this.maxWidth) {
              this.inputs.width = this.maxWidth;
            }
          },
          'inputs.depth': function (newVal) {
            if(newVal > this.maxDepth) {
              this.inputs.depth = this.maxDepth;
            }
          },
          'inputs.frameWidth': function (newVal) {
            if(newVal > this.maxFrameWidth) {
              this.inputs.frameWidth = this.maxFrameWidth;
            }
          },
        }
      }).mount(this.$el[0]);
    });
  }
}

export default ConfiguratorApp;